import React, { useState } from 'react';
import { faceAttributeValue } from '@nuralogix.ai/web-measurement-embedded-app';
import { Footer } from '@/shared/components/layout/Footer';

type FormErrors = {
    age?: string;
    heightCm?: string;
    weightKg?: string;
    sex?: string;
};

const Step1 = ({ data, onNext }) => {
    // Estado para manejar los valores del formulario
    // Inicializa con 'data' que viene del padre (allData)
    const [formData, setFormData] = useState({
        age: data.age || null,
        sex: data.sex || null,
        heightCm: data.heightCm || null,
        weightKg: data.weightKg || null
    });

    // Estado para validación simple (mostrar/ocultar mensaje de error)
    const [errors, setErrors] = useState<FormErrors>({});

    const handleKeyDown = (e) => {
        // Solo permitir números, borrar, tabulador y flechas
        const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];
        if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
            e.preventDefault();
        }
    };

    const handleKeyDownWeight = (e) => {
        const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Enter', '.', ','];
        const isNumber = /[0-9]/.test(e.key);

        // Si no es número y no está en la lista de permitidos (que incluye punto y coma), bloquear
        if (!isNumber && !allowedKeys.includes(e.key)) {
            e.preventDefault();
        }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const { name } = e.target;
        const newErrors = { ...errors };

        if (name === 'age') {
            const val = Number(formData.age);
            if (!formData.age || isNaN(val) || val < 18 || val > 100) {
                newErrors.age = "Por favor ingresa un valor entre 18 y 100";
            } else {
                delete newErrors.age;
            }
        }

        if (name === 'heightCm') {
            const val = Number(formData.heightCm);
            if (!formData.heightCm || isNaN(val) || val < 120 || val > 220) {
                newErrors.heightCm = "No utilices puntos ni comas, solo número enteros";
            } else {
                delete newErrors.heightCm;
            }
        }

        if (name === 'weightKg') {
            const val = Number(formData.weightKg);
            if (!formData.weightKg || isNaN(val) || val < 30 || val > 300) {
                newErrors.weightKg = "Por favor ingresa un valor entre 30 y 300";
            } else {
                delete newErrors.weightKg;
            }
        }

        if (name === 'sex') {
            const val = Number(formData.sex);
            if (!formData.sex || isNaN(val) || val < 2 || val > 3) {
                newErrors.sex = "Por favor selecciona una opción";
            } else {
                delete newErrors.sex;
            }
        }
        setErrors(newErrors);
    };

    const handleContinue = () => {
        let weightInput = formData.weightKg ? formData.weightKg.toString().trim() : "";
        if (weightInput.endsWith('.')) {
            weightInput = weightInput.slice(0, -1);
        }

        const ageValue = formData.age !== "" ? Number(formData.age) : 0;
        const heightValue = formData.heightCm !== "" ? Number(formData.heightCm) : 0;
        const weightValue = formData.weightKg !== "" ? Number(formData.weightKg) : 0;
        const sexValue = formData.sex !== "" ? Number(formData.sex) : 0;
        const newErrors: FormErrors = {};

        if (!formData.age || ageValue < 18 || ageValue > 100) {
            newErrors.age = "Por favor ingresa un valor entre 18 y 100";
        }
        if (!formData.heightCm || heightValue < 120 || heightValue > 220) {
            newErrors.heightCm = "Por favor ingresa un valor entre 120 y 220";
        }
        if (!formData.weightKg || isNaN(weightValue) || weightValue < 30 || weightValue > 300) {
            newErrors.weightKg = "Por favor ingresa un valor entre 30 y 300";
        }
        if (!formData.sex || isNaN(sexValue) || sexValue < 2 || sexValue > 3) {
            newErrors.sex = "Por favor selecciona una opción";
        }

        // Si hay algún error, los guardamos y detenemos
        if (Object.keys(newErrors).length > 0) {
            setFormData(prev => ({ ...prev, weightKg: weightInput }));
            setErrors(newErrors);
            return;
        }

        // Si todo está bien, limpiamos y avanzamos
        setErrors({});
        onNext({
            age: ageValue,
            sex: sexValue,
            heightCm: heightValue,
            weightKg: weightValue
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "weightKg") {
            const cleanValue = value.replace(',', '.');

            //Evitar más de un punto
            if ((cleanValue.match(/\./g) || []).length > 1) return;

            // Limitar a un solo decimal
            const parts = cleanValue.split('.');
            if (parts[1] && parts[1].length > 1) return;

            setFormData(prev => ({ ...prev, [name]: cleanValue }));
        } else {
            // Para edad y altura, simplemente guardamos el valor (que el onKeyDown ya filtró)
            setFormData(prev => ({ ...prev, [name]: value }));
        }

        // Limpiamos el error del campo específico mientras el usuario escribe
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    return (
        <div className="vitals-wrapper">
            <div className="container-vitals-top">
                <section className="section-vitals-top form-steps">

                    {/* Barra de Progreso */}
                    <div className="steps-wrapper step-1">
                        <div className="steps-bar">
                            <div className="steps-progress"></div>
                        </div>
                        <div className="steps-labels">
                            <span>Paso 1</span>
                            <span>Paso 2</span>
                            <span>Paso 3</span>
                        </div>
                    </div>

                    <div className="form-vitals">
                        {/* <div className='arrow-back'>
                            <div className="icon icon_enlinea_flechita-izq back-card" onClick={() => window.history.back()}>
                            </div>
                        </div> */}


                        <div className="title-form-vitals">
                            <i className="icon icon_enlinea_usuario"></i>
                            Cuestionario de perfil de usuario
                        </div>

                        <form>
                            {/* Edad */}
                            <div className="mb-3">
                                <label className="form-label">Edad</label>
                                <div className="input-group">
                                    <input
                                        type="number"
                                        name="age"
                                        className="form-control"
                                        placeholder="Escribe cuántos años tienes"
                                        min={18}
                                        max={100}
                                        value={formData.age ?? ""}
                                        onChange={handleChange}
                                        onKeyDown={handleKeyDown}
                                        onBlur={handleBlur}
                                    />
                                    <input type="text" className="form-control" value="Años" readOnly />
                                </div>
                                {errors.age && <p className="msj-error mt-2">{errors.age}</p>}
                            </div>

                            {/* Sexo biológico */}
                            <div className="mb-3 biolog-input">
                                <label className="form-label">Sexo biológico</label>
                                <select
                                    name="sex"
                                    className='form-control'
                                    value={formData.sex}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                >
                                    <option value={null}>Selecciona tu sexo biológico</option>
                                    <option value={faceAttributeValue.SEX_ASSIGNED_FEMALE_AT_BIRTH}>Femenino</option>
                                    <option value={faceAttributeValue.SEX_ASSIGNED_MALE_AT_BIRTH}>Masculino</option>
                                </select>
                            </div>
                            {errors.sex && <p className="msj-error mt-2">{errors.sex}</p>}

                            {/* Altura */}
                            <div className="mb-3">
                                <label className="form-label">Altura</label>
                                <div className="input-group">
                                    <input
                                        type="number"
                                        name="heightCm"
                                        className="form-control"
                                        placeholder="Escribe tu altura en centímetros"
                                        min={120}
                                        max={220}
                                        value={formData.heightCm ?? ""}
                                        onChange={handleChange}
                                        onKeyDown={handleKeyDown}
                                        onBlur={handleBlur}
                                    />
                                    <input type="text" className="form-control" value="Centímetros" readOnly />
                                </div>
                                {errors.heightCm && <p className="msj-error mt-2">{errors.heightCm}</p>}
                            </div>

                            {/* Peso */}
                            <div className="mb-3">
                                <label className="form-label">Peso</label>
                                <div className="input-group">
                                    <input
                                        type="number"
                                        name="weightKg"
                                        className="form-control"
                                        placeholder="Escribe tu peso en kilogramos"
                                        value={formData.weightKg ?? ""}
                                        onChange={handleChange}
                                        onKeyDown={handleKeyDownWeight}
                                        onBlur={handleBlur}
                                    />
                                    <input type="text" className="form-control" value="Kilogramos" readOnly />
                                </div>
                                {errors.weightKg && <p className="msj-error mt-2">{errors.weightKg}</p>}
                            </div>

                            <div className="btn-form-vitals">
                                <button type="button" className="btn vitals-primary" onClick={handleContinue}>
                                    Continuar
                                </button>
                            </div>
                        </form>
                    </div>
                </section>
            </div>

            
            <Footer/>
        </div>
    );
};

export { Step1 };
